import React from 'react';
import { HostAuditSummary } from '../services/hostService.ts';

interface HostAuditProps {
  summary: HostAuditSummary;
  loading: boolean;
  unmappedCandidates: { place: string; count: number; source: string; sampleEventUrl: string }[];
  unmappedLoading: boolean;
  onFilterAudit: (filter: '' | 'missingGeo' | 'missingCover' | 'svgCover' | 'missingDesc' | 'missingUrl') => void;
}

export const HostAudit: React.FC<HostAuditProps> = ({
  summary,
  loading,
  unmappedCandidates,
  unmappedLoading,
  onFilterAudit,
}) => {
  if (loading) {
    return <div className="host-audit-loading">Computing audit...</div>;
  }

  const cards: { label: string; value: number; filterKey: '' | 'missingGeo' | 'missingCover' | 'svgCover' | 'missingDesc' | 'missingUrl'; severity: 'good' | 'warn' | 'bad' }[] = [
    { label: 'Total Hosts', value: summary.totalHosts, filterKey: '', severity: 'good' },
    { label: 'Missing Geo', value: summary.missingGeo, filterKey: 'missingGeo', severity: summary.missingGeo > 0 ? 'bad' : 'good' },
    { label: 'Missing Cover', value: summary.missingCover, filterKey: 'missingCover', severity: summary.missingCover > 0 ? 'bad' : 'good' },
    { label: 'SVG Cover', value: summary.svgCover, filterKey: 'svgCover', severity: summary.svgCover > 0 ? 'warn' : 'good' },
    { label: 'Weak Description', value: summary.missingDescription, filterKey: 'missingDesc', severity: summary.missingDescription > 0 ? 'warn' : 'good' },
    { label: 'Missing URL', value: summary.missingUrl, filterKey: 'missingUrl', severity: summary.missingUrl > 0 ? 'warn' : 'good' },
  ];

  return (
    <div className="host-audit-section">
      <h3 className="host-audit-title">Host Quality Audit</h3>
      <div className="host-audit-cards">
        {cards.map((card) => (
          <button
            key={card.label}
            className={`host-audit-card severity-${card.severity}`}
            onClick={() => onFilterAudit(card.filterKey)}
            title={card.filterKey ? `Click to filter hosts table` : ''}
          >
            <span className="host-audit-card-value">{card.value}</span>
            <span className="host-audit-card-label">{card.label}</span>
          </button>
        ))}
      </div>

      {/* Unmapped place candidates */}
      <div className="host-audit-unmapped">
        <h4>Unmapped Place Candidates</h4>
        <p className="host-audit-unmapped-hint">
          Recent posts without hostID whose place does not match any existing host title.
        </p>
        {unmappedLoading ? (
          <div className="host-audit-loading">Scanning recent posts...</div>
        ) : unmappedCandidates.length === 0 ? (
          <p className="host-audit-none">No strong unmapped candidates found.</p>
        ) : (
          <div className="host-audit-unmapped-table-wrapper">
            <table className="host-audit-unmapped-table">
              <thead>
                <tr>
                  <th>Place</th>
                  <th>Posts</th>
                  <th>Source</th>
                  <th>Sample URL</th>
                </tr>
              </thead>
              <tbody>
                {unmappedCandidates.map((c, i) => (
                  <tr key={i}>
                    <td>{c.place}</td>
                    <td className="hosts-cell-center">{c.count}</td>
                    <td>{c.source || '-'}</td>
                    <td className="hosts-cell-url">
                      {c.sampleEventUrl ? (
                        <a href={c.sampleEventUrl} target="_blank" rel="noopener noreferrer">
                          {c.sampleEventUrl.slice(0, 40)}...
                        </a>
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
